import { disableVerticalSwipes, enableVerticalSwipes } from '@telegram-apps/sdk-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/cn';
import {
  SPRING_DEFAULT,
  SPRING_MOMENTUM,
  VelocityTracker,
  animateSpring,
  prefersReducedMotion,
  project,
  rubberband,
  type SpringAnimation,
} from '@/lib/motion';
import { texts } from '@/lib/texts';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Кнопки внизу шторки */
  footer?: ReactNode;
}

/** Сдвиг до начала жеста, после которого он считается тягой, а не нажатием. */
const DRAG_THRESHOLD_PX = 8;
/** Скорость, начиная с которой решает направление броска, а не положение. */
const FLICK_VELOCITY = 300;
/** Замедление для проекции: шторка тяжелее скролла (0.998), долетает ближе. */
const DECELERATION = 0.99;
/** Длительность проявления при `prefers-reduced-motion` — вместо сдвига. */
const FADE_MS = 200;

/** Захват указателя: тяга продолжается и за пределами шторки. Указатель уже мог пропасть — не падаем. */
function capturePointer(el: HTMLElement, pointerId: number): void {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    /* указатель уже отпущен или синтетический — жест всё равно дойдёт по событиям на панели */
  }
}

/**
 * Нижняя шторка: подтверждение, выбор варианта, детали. Закрывается по фону, Esc и жестом вниз.
 *
 * Движение по принципам Apple: шторка идёт за пальцем 1:1 с того места, где её взяли, выше
 * верхнего положения сопротивляется как резина, при отпускании продолжает со скоростью пальца
 * и садится туда, куда её бросили (проекция инерции), а не туда, где отпустили. Любую анимацию
 * (в том числе закрытие) можно перехватить пальцем и повернуть обратно. Уходит она тем же путём,
 * каким пришла. При `prefers-reduced-motion` — короткое проявление без сдвига.
 */
export function Sheet({ open, onClose, title, children, footer }: SheetProps) {
  // Шторка остаётся в дереве, пока уезжает; содержимое на это время замораживается,
  // иначе родитель, обнулив выбор, опустошит её на середине пути.
  const [mounted, setMounted] = useState(open);
  const frozen = useRef({ title, children, footer });
  useLayoutEffect(() => {
    if (open) frozen.current = { title, children, footer };
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  /** Текущее смещение шторки: 0 — открыта, `height` — за краем экрана. */
  const y = useRef(0);
  const height = useRef(0);
  const anim = useRef<SpringAnimation | null>(null);
  /** Родитель уже закрыл шторку — жест перехватить нельзя, состояния в родителе больше нет. */
  const closingByParent = useRef(false);
  const reduced = useRef(false);

  const apply = useCallback((next: number) => {
    y.current = next;
    const h = height.current || 1;
    const panel = panelRef.current;
    const scrim = scrimRef.current;
    if (panel) panel.style.transform = `translate3d(0, ${next}px, 0)`;
    if (scrim) scrim.style.opacity = String(Math.min(1, Math.max(0, 1 - next / h)));
  }, []);

  const stopAnim = useCallback(() => {
    const live = anim.current?.stop() ?? { value: y.current, velocity: 0 };
    anim.current = null;
    return live;
  }, []);

  const springTo = useCallback(
    (target: number, velocity: number, momentum: boolean, onComplete?: () => void) => {
      stopAnim();
      anim.current = animateSpring({
        from: { value: y.current, velocity },
        to: target,
        config: momentum ? SPRING_MOMENTUM : SPRING_DEFAULT,
        onUpdate: apply,
        onComplete: () => {
          anim.current = null;
          onComplete?.();
        },
      });
    },
    [apply, stopAnim],
  );

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // Вход и выход — по одному пути (снизу и вниз), из текущего положения.
  useLayoutEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    if (!panel) return;
    reduced.current = prefersReducedMotion();
    if (open) {
      closingByParent.current = false;
      height.current = panel.offsetHeight;
      if (reduced.current) {
        apply(0);
        panel.style.opacity = '0';
        if (scrimRef.current) scrimRef.current.style.opacity = '0';
        const id = requestAnimationFrame(() => {
          panel.style.opacity = '1';
          if (scrimRef.current) scrimRef.current.style.opacity = '1';
        });
        return () => cancelAnimationFrame(id);
      }
      // Первое открытие — из-за края; повторное `open` при уже видимой шторке — с места
      if (anim.current === null && y.current === 0) apply(height.current);
      springTo(0, 0, false);
      return;
    }
    // Родитель закрыл: шторка уже за краем (закрыта жестом) — просто снять; иначе увезти
    closingByParent.current = true;
    if (y.current >= height.current - 0.5 && anim.current === null) {
      setMounted(false);
      return;
    }
    if (reduced.current) {
      panel.style.opacity = '0';
      if (scrimRef.current) scrimRef.current.style.opacity = '0';
      const id = window.setTimeout(() => setMounted(false), FADE_MS);
      return () => window.clearTimeout(id);
    }
    springTo(height.current, stopAnim().velocity, false, () => setMounted(false));
  }, [open, mounted, apply, springTo, stopAnim]);

  useEffect(
    () => () => {
      stopAnim();
    },
    [stopAnim],
  );

  // Esc, блокировка прокрутки под шторкой, вертикальные свайпы Telegram (иначе тяга вниз
  // закрывает само приложение), фокус внутрь диалога.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    disableVerticalSwipes.ifAvailable();
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      enableVerticalSwipes.ifAvailable();
      if (previouslyFocused instanceof HTMLElement)
        previouslyFocused.focus({ preventScroll: true });
    };
  }, [mounted, onClose]);

  // Длинное содержимое прокручивается само — тогда тянуть шторку можно только за верх.
  useLayoutEffect(() => {
    if (!mounted) return;
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mounted, open]);

  // Жест: pointerdown → ждём порог → тянем 1:1 → отпускаем с проекцией инерции.
  const drag = useRef<{
    pointerId: number;
    startClientY: number;
    startY: number;
    committed: boolean;
    tracker: VelocityTracker;
  } | null>(null);
  const suppressClick = useRef(false);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (closingByParent.current || reduced.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const inContent = contentRef.current?.contains(e.target as Node) ?? false;
    if (overflowing && inContent) return;
    // Перехват: летящая шторка останавливается там, где она сейчас, и дальше её ведёт палец
    const wasAnimating = anim.current !== null;
    const live = stopAnim();
    const tracker = new VelocityTracker();
    tracker.push(live.value, e.timeStamp);
    drag.current = {
      pointerId: e.pointerId,
      startClientY: e.clientY,
      startY: live.value,
      committed: wasAnimating,
      tracker,
    };
    if (wasAnimating) {
      suppressClick.current = true;
      capturePointer(e.currentTarget, e.pointerId);
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dy = e.clientY - d.startClientY;
    if (!d.committed) {
      if (Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      d.committed = true;
      suppressClick.current = true;
      capturePointer(e.currentTarget, e.pointerId);
    }
    const raw = d.startY + dy;
    const next = raw < 0 ? rubberband(raw, height.current) : raw;
    d.tracker.push(next, e.timeStamp);
    apply(next);
  };

  const release = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (!d.committed) return;
    const v = d.tracker.velocity(e.timeStamp);
    const h = height.current;
    // Направление решает скорость броска; без броска — куда долетит по инерции
    const shouldClose =
      Math.abs(v) >= FLICK_VELOCITY ? v > 0 : y.current + project(v, DECELERATION) > h / 2;
    if (shouldClose) {
      springTo(h, v, false, onClose);
    } else {
      springTo(0, v, Math.abs(v) >= FLICK_VELOCITY);
    }
  };

  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  if (!mounted) return null;
  const view = open ? { title, children, footer } : frozen.current;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="presentation">
      <button
        ref={scrimRef}
        type="button"
        aria-label={texts.common.close}
        onClick={onClose}
        className="sheet-scrim absolute inset-0 bg-black/60"
        style={{ opacity: 0 }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={release}
        onPointerCancel={release}
        onClickCapture={onClickCapture}
        className={cn(
          // На низких экранах (320×568) 85 % высоты хватало на два пункта из пяти — там шторка почти во всю высоту
          'bg-surface rounded-t-card sheet-panel relative flex max-h-[85dvh] flex-col outline-none [@media(max-height:700px)]:max-h-[94dvh]',
          !open && 'pointer-events-none',
        )}
        style={{
          paddingBottom: 'calc(var(--safe-bottom) + 1rem)',
          touchAction: overflowing ? 'pan-y' : 'none',
          willChange: 'transform',
        }}
      >
        <div className="shrink-0 px-4 pt-3" style={{ touchAction: 'none' }}>
          <div className="bg-border mx-auto mb-3 h-1 w-10 rounded-full" aria-hidden="true" />
          {view.title ? (
            <h2 className="mb-3 text-lg font-bold tracking-tight">{view.title}</h2>
          ) : null}
        </div>
        <div
          ref={contentRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 text-base"
        >
          {view.children}
        </div>
        {view.footer ? (
          <div className="mt-4 flex shrink-0 flex-col gap-2 px-4">{view.footer}</div>
        ) : null}
      </div>
    </div>
  );
}
